import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Avatar,
} from '@mui/material';
import {
  Lightbulb as LightbulbIcon,
  Book as BookIcon,
  CalendarMonth as CalendarIcon,
  Download as DownloadIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  LockOutlined as LockIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePasskey } from '../contexts/PasskeyContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { logOut } from '../firebase/config';

export type MenuSection = 'thoughts' | 'diary' | 'journals' | 'download';

interface SidebarProps {
  open: boolean;
  selectedSection: MenuSection;
  onSectionSelect: (section: MenuSection) => void;
  onClose?: () => void;
}

export const DRAWER_WIDTH = 280;

const Sidebar: React.FC<SidebarProps> = ({
  open,
  selectedSection,
  onSectionSelect,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { clearPasskey } = usePasskey();
  const { mode, toggleTheme } = useThemeMode();

  const handleSectionClick = (section: MenuSection) => {
    onSectionSelect(section);
    if (isMobile && onClose) {
      onClose();
    }
  };

  const handleLock = () => {
    clearPasskey();
    if (isMobile && onClose) {
      onClose();
    }
  };

  const handleLogout = async () => {
    try {
      await logOut();
    } catch (error) {
      console.error('Error logging out:', error);
    }
    if (isMobile && onClose) {
      onClose();
    }
  };

  const handleSettings = () => {
    navigate('/settings/passkey');
    if (isMobile && onClose) {
      onClose();
    }
  };

  const menuItems = [
    {
      id: 'thoughts' as MenuSection,
      label: 'Thoughts',
      icon: <LightbulbIcon />,
      description: 'Quick notes & ideas',
    },
    {
      id: 'diary' as MenuSection,
      label: 'Diary',
      icon: <CalendarIcon />,
      description: 'Daily entries by date',
    },
    {
      id: 'journals' as MenuSection,
      label: 'Journals',
      icon: <BookIcon />,
      description: 'Long-form writing',
    },
  ];

  // Get user initials for avatar
  const getUserInitials = () => {
    if (currentUser?.displayName) {
      return currentUser.displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
    if (currentUser?.email) {
      return currentUser.email[0].toUpperCase();
    }
    return 'U';
  };

  const drawerContent = (
    <Box sx={{ overflow: 'auto', py: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* User Profile Section */}
      {currentUser && (
        <>
          <Box sx={{ px: 2, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  width: 40,
                  height: 40,
                  fontSize: '1rem',
                  color: 'primary.contrastText',
                }}
              >
                {getUserInitials()}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {currentUser.displayName || 'User'}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}
                >
                  {currentUser.email}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Divider sx={{ mb: 1 }} />
        </>
      )}

      {/* Main Menu Sections */}
      <Box sx={{ px: 2, pb: 1 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          MENU
        </Typography>
      </Box>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              selected={selectedSection === item.id}
              onClick={() => handleSectionClick(item.id)}
              sx={{
                mx: 1,
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: selectedSection === item.id ? 'primary.contrastText' : 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                secondary={item.description}
                primaryTypographyProps={{
                  fontWeight: selectedSection === item.id ? 600 : 400,
                  color: selectedSection === item.id ? 'primary.contrastText' : 'inherit',
                }}
                secondaryTypographyProps={{
                  variant: 'caption',
                  sx: { 
                    opacity: 0.7,
                    color: selectedSection === item.id ? 'primary.contrastText' : 'inherit',
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Download Section */}
      <Divider sx={{ my: 1 }} />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            selected={selectedSection === 'download'}
            onClick={() => handleSectionClick('download')}
            sx={{
              mx: 1,
              borderRadius: 2,
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: selectedSection === 'download' ? 'primary.contrastText' : 'inherit',
              }}
            >
              <DownloadIcon />
            </ListItemIcon>
            <ListItemText
              primary="Download All"
              secondary="Export your data"
              primaryTypographyProps={{
                fontWeight: selectedSection === 'download' ? 600 : 400,
                color: selectedSection === 'download' ? 'primary.contrastText' : 'inherit',
              }}
              secondaryTypographyProps={{
                variant: 'caption',
                sx: { 
                  opacity: 0.7,
                  color: selectedSection === 'download' ? 'primary.contrastText' : 'inherit',
                },
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Settings Section - At bottom */}
      <Divider sx={{ my: 1 }} />
      <Box sx={{ px: 2, pb: 1 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          SETTINGS
        </Typography>
      </Box>
      <List>
        {/* Theme Toggle */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={toggleTheme}
            sx={{ mx: 1, borderRadius: 2 }}
          >
            <ListItemIcon>
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </ListItemIcon>
            <ListItemText
              primary={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
              primaryTypographyProps={{ fontSize: '0.875rem' }}
            />
          </ListItemButton>
        </ListItem>

        {/* Lock App */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLock}
            sx={{ mx: 1, borderRadius: 2 }}
          >
            <ListItemIcon>
              <LockIcon />
            </ListItemIcon>
            <ListItemText
              primary="Lock App"
              primaryTypographyProps={{ fontSize: '0.875rem' }}
            />
          </ListItemButton>
        </ListItem>

        {/* Passkey Settings */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleSettings}
            sx={{ mx: 1, borderRadius: 2 }}
          >
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText
              primary="Passkey Settings"
              primaryTypographyProps={{ fontSize: '0.875rem' }}
            />
          </ListItemButton>
        </ListItem>

        {/* Logout */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              mx: 1,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: 'error.main',
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
                '& .MuiListItemText-primary': {
                  color: 'white',
                },
              },
            }}
          >
            <ListItemIcon sx={{ color: 'error.main' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                fontSize: '0.875rem',
                color: 'error.main',
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  // Mobile: temporary drawer (overlay)
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            pt: '64px', // Add padding top for header
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Desktop: permanent drawer (always visible, takes up space in layout)
  return (
    <Drawer
      variant="permanent"
      open
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          top: '64px',
          height: 'calc(100% - 64px)',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
